import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * The sticky button forwards to the original Shopify product form. It never
 * creates its own cart request or chooses a replacement variant.
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];
  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #targetStateObserver = null;
  #formStateObserver = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #variantPending = false;
  #submitting = false;
  #footerVisible = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener('focusin', this.#syncVisibility, { signal });
    document.addEventListener('focusout', this.#syncVisibility, { signal });
    // Native modal dialogs stay in the top layer; update keyboard availability as well.
    document.addEventListener('toggle', this.#syncVisibility, { signal, capture: true });
    window.addEventListener('resize', this.#syncVisibility, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', this.#syncVisibility, { signal, passive: true });
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    this.#setupIntersectionObserver();
    this.#syncButton();
    this.#syncVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#targetStateObserver?.disconnect();
    this.#formStateObserver?.disconnect();
    this.#abortController.abort();
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]');
    if (!productForm || !this.#targetAddToCartButton || typeof IntersectionObserver === 'undefined') return;
    // Watch the actual primary button, rather than the whole quantity/payment block.
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#syncVisibility);
    this.#buyButtonsIntersectionObserver.observe(this.#targetAddToCartButton);
    this.#targetStateObserver?.disconnect();
    this.#targetStateObserver = new MutationObserver(this.#syncButton);
    this.#targetStateObserver.observe(this.#targetAddToCartButton, {
      attributes: true, attributeFilter: ['disabled', 'aria-disabled'],
    });
    this.#formStateObserver?.disconnect();
    this.#formStateObserver = new MutationObserver(() => {
      this.#submitting = productForm.dataset.armmySubmitting === 'true';
      this.#syncButton();
    });
    this.#formStateObserver.observe(productForm, { attributes: true, attributeFilter: ['data-armmy-submitting'] });
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    this.#mainBottomObserver?.disconnect();
    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(([entry]) => {
        this.#footerVisible = Boolean(entry?.isIntersecting);
        this.#syncVisibility();
      }, { rootMargin: '100px 0px 0px 0px' });
      this.#mainBottomObserver.observe(footer);
    }
  }

  handleAddToCartClick = () => {
    const productForm = this.#getProductForm();
    const button = this.#targetAddToCartButton;
    if (!button || button.disabled || this.#variantPending || this.#submitting ||
        this.refs.addToCartButton.disabled || productForm?.dataset.armmySubmitting === 'true') return;
    // Native validation, quantity, selected ID, cart rendering and checkout remain authoritative.
    this.#submitting = true;
    this.#syncButton();
    button.click();
    // Invalid quantity/form submissions never start a request and should remain correctable.
    if (productForm?.dataset.armmySubmitting !== 'true') {
      this.#submitting = false;
      this.#syncButton();
    }
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;
    const next = event.detail.data.html.querySelector('sticky-add-to-cart');
    const nextBar = next?.querySelector('[ref="stickyBar"]');
    if (!nextBar) return;
    const visible = this.refs.stickyBar.dataset.stuck;
    morph(this.refs.stickyBar, nextBar, { childrenOnly: true });
    this.refs.stickyBar.dataset.stuck = visible;
    this.dataset.variantAvailable = next.dataset.variantAvailable;
    this.dataset.currentVariantId = event.detail.resource?.id || '';
    this.#variantPending = false;
    this.#setupIntersectionObserver();
    if (!event.detail.resource) {
      const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
      const label = this.querySelector('.sticky-add-to-cart__variant');
      const values = picker ? [...picker.querySelectorAll('input:checked, select option:checked')]
        .map((option) => option.value).filter(Boolean).join(' / ') : '';
      if (label && values) label.textContent = values;
    }
    this.#syncButton();
    this.#syncVisibility();
  };

  #handleVariantSelected = () => {
    // This event carries an OPTION value ID, not a purchasable variant ID.
    // Block purchasing until Shopify's response supplies the resolved variant.
    this.#variantPending = true;
    this.#syncButton();
  };

  #handleCartAddComplete = (event) => {
    const productForm = this.#getProductForm();
    const data = event.detail?.data;
    if (data?.productId && data.productId !== this.dataset.productId) return;
    if (!data?.productId && event.detail?.sourceId &&
        ![productForm?.id, productForm?.querySelector('form')?.id, this.dataset.currentVariantId]
          .includes(event.detail.sourceId)) return;
    this.#submitting = productForm?.dataset.armmySubmitting === 'true';
    this.#syncButton();
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#syncButton();
  };

  #syncButton = () => {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = this.dataset.variantAvailable === 'true';
    addToCartButton.disabled = !available || this.#variantPending || this.#submitting ||
      Boolean(this.#targetAddToCartButton?.disabled);
    addToCartButton.setAttribute('aria-busy', String(this.#submitting));
    quantityNumber.textContent = String(this.#currentQuantity);
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
  };

  #syncVisibility = () => {
    const rect = this.#targetAddToCartButton?.getBoundingClientRect();
    const active = document.activeElement;
    const editing = active?.matches?.('input, select, textarea, [contenteditable="true"]');
    const keyboard = window.visualViewport && window.visualViewport.height < window.innerHeight * 0.75;
    const show = Boolean(this.dataset.variantAvailable === 'true' && this.#buyButtonsIntersectionObserver && rect && rect.bottom <= 0 &&
      !this.#footerVisible && !editing && !keyboard && !document.querySelector('dialog[open]'));
    this.refs.stickyBar.dataset.stuck = String(show);
    this.refs.stickyBar.inert = !show;
    this.refs.stickyBar.setAttribute('aria-hidden', String(!show));
  };

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    return this.closest('.shopify-section')?.querySelector(`product-form-component[data-product-id="${productId}"]`) || null;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
